<template>
  <list-content v-if="order">
    <v-col cols="12">
      <v-form ref="form" v-model="validForm" lazy-validation>
        <v-row v-if="orderStatus === PortalOrderStatus.SENT" density="comfortable">
          <v-col>
            <sf-alert-success closable @click:close="removeQueryParams()">
              {{ $t('order.orderSent') }}
            </sf-alert-success>
          </v-col>
        </v-row>

        <v-row v-if="isTopiExpired" density="comfortable">
          <v-col>
            <sf-alert-warning>
              {{ $t('topiExpiredHint') }}
            </sf-alert-warning>
          </v-col>
        </v-row>

        <v-row v-if="order.approvalRequest" density="comfortable">
          <v-col>
            <approval-request-info
              :initiator="order.initiator"
              :approval-request="order.approvalRequest"
            />
          </v-col>
        </v-row>

        <v-row density="comfortable">
          <v-col cols="12" sm="6" md="">
            <order-address-and-contact-data
              ref="billing-address-and-contact-data"
              :address="order.billingAddress as ShopAddress"
              :address-type="AddressType.BILLING"
              :address-selection="order.billingAddressSelection || undefined"
              :contact-data="order.billingContactData || undefined"
              :readonly="!isPreparedOrder"
              @select-address="updateBillingAddress"
              @update-contact-data="updateBillingContactData"
            />
          </v-col>
          <v-col cols="12" sm="6" md="">
            <order-address-and-contact-data
              ref="delivery-address-and-contact-data"
              :address="order.deliveryAddress as ShopAddress"
              :address-type="AddressType.DELIVERY"
              :address-selection="order.deliveryAddressSelection || undefined"
              :contact-data="order.deliveryContactData || undefined"
              :readonly="!isPreparedOrder"
              @select-address="updateDeliveryAddress"
              @update-contact-data="updateDeliveryContactData"
            />
          </v-col>
          <v-col cols="12" sm="">
            <order-comment
              :editable="isPreparedOrder"
              :max-length="orderingConditions.maxCommentLength"
              :order-comment="order.orderComment || undefined"
              :partial-shipment-enabled="orderingConditions.isPartialShipmentEnabled"
              :partial-shipment-select="order.hasPartialShipment"
              @order-comment-changed="updateOrderComment"
              @partial-shipment-selection-changed="updatePartialShipment"
            />
          </v-col>
          <v-col v-if="order.paymentMethod" cols="12" sm="">
            <mobex-payment-method v-model="order.paymentMethod" read-only />
          </v-col>
        </v-row>

        <v-row density="comfortable">
          <v-col>
            <order-numbers
              :customer-number="order.customerNumber"
              :customer-order-id="order.customerOrderId || undefined"
              :customer-order-id-required="orderingConditions.customerOrderIdRequired"
              :order-number="order.orderNumber"
              :read-only="!isPreparedOrder"
              :is-custom-order-number-enabled="portalSettings.isCustomOrderNumberEnabled"
              :show-customer-order-id="
                (isPreparedOrder && orderingConditions.showCustomerOrderId) ||
                order.customerOrderId !== undefined
              "
              @customer-order-id-changed="updateCustomerOrderNumber"
              @order-number-changed="updateOrderNumber"
            />
          </v-col>
        </v-row>
      </v-form>

      <v-divider class="my-2" />

      <v-row density="comfortable">
        <v-col>
          <order-position
            v-for="item in order.items"
            :key="item.id"
            :editable="isPreparedOrder"
            :order-item="item"
            :last-item="onlyOneItem"
            :order-id="order.id"
            :show-topi-price="isWaitingForTopiCheckout"
          />
        </v-col>
      </v-row>

      <v-divider class="my-2" />

      <order-validation-status
        v-if="isPreparedOrder"
        :validation-status="detailedOrder.validationStatus"
      />

      <v-row v-if="order.isTopiOrder" density="comfortable">
        <v-col v-if="order.topiCheckoutUrl">
          <sf-large-button :block="$vuetify.display.xs" :href="order.topiCheckoutUrl">
            {{ $t('openTopiCheckout') }}
          </sf-large-button>
        </v-col>
        <v-col>
          <order-info
            :order-rejection-date="rejectionDate || undefined"
            :order-last-updated="order.lastUpdated"
            :order-sent-date="order.sentDate || undefined"
            :order-sum-information="order.orderSumInformation"
            :shipping-cost="order.shippingCost"
            :quantity-surcharge="order.quantitySurcharge || undefined"
            :hide-prices="isWaitingForTopiCheckout || isTopiExpired"
          />
        </v-col>
      </v-row>

      <v-row
        v-else-if="
          (isPreparedOrder && order.orderLimitExceedance == OrderLimitExceedance.KEPT) ||
          !isPreparedOrder
        "
        density="comfortable"
        justify="space-between"
      >
        <v-col
          v-if="isPreparedOrder"
          ref="prepared-order-buttons"
          cols="12"
          md="6"
          class="d-flex flex-column"
        >
          <sf-text v-if="safeCheckoutTerms" class="order-first mb-1">
            <div v-html="safeCheckoutTerms" />
          </sf-text>
          <div class="d-flex flex-column flex-sm-row order-last order-md-first flex-wrap">
            <sf-large-button
              id="order-button"
              class="mr-2 mb-1 flex-grow-1 flex-md-grow-0"
              :block="$vuetify.display.xs"
              :disabled="
                !validForm ||
                !addressValid ||
                !allCommentsValid ||
                !isApproveableOrder ||
                detailedOrder.validationStatus.validationResult === OrderValidationResult.ERROR
              "
              :loading="sending"
              @click="sendOrder()"
              >{{ $t('order.sendOrder') }}
            </sf-large-button>
            <rejection-dialog
              class="mr-0 mr-md-2 mt-2 mt-md-0 order-last order-md-first"
              :rejecting="rejecting"
              :type="'ORDER'"
              :disabled="(order.approvalRequest && !isApproveableOrder) || false"
              @reject="declineOrder"
            />
          </div>
        </v-col>
        <v-col>
          <order-info
            :order-rejection-date="rejectionDate || undefined"
            :order-last-updated="order.lastUpdated"
            :order-sent-date="order.sentDate || undefined"
            :order-sum-information="order.orderSumInformation"
            :shipping-cost="order.shippingCost || undefined"
            :quantity-surcharge="order.quantitySurcharge || undefined"
            class="flex-grow-1 order-first"
          />
        </v-col>
      </v-row>

      <v-row
        v-else-if="isPreparedOrder && order.orderLimitExceedance != OrderLimitExceedance.KEPT"
        no-gutters
      >
        <v-col class="d-flex flex-column">
          <order-limit-warning
            :no-order-right="order.orderLimitExceedance == OrderLimitExceedance.NO_ORDER_RIGHT"
            :approvers="order.orderLimitApprovers || []"
            :costs="order.orderSumInformation.grossSum"
            :order-limit-value="order.orderLimitValue || undefined"
            :loading="sending"
            :disabled="!validForm || !addressValid || !allCommentsValid"
            @request-order="requestOrder"
          >
            <order-info
              class="flex-grow-1"
              :order-rejection-date="rejectionDate || undefined"
              :order-sent-date="order.sentDate || undefined"
              :order-last-updated="order.lastUpdated"
              :shipping-cost="order.shippingCost || undefined"
              :quantity-surcharge="order.quantitySurcharge || undefined"
              :order-sum-information="order.orderSumInformation"
            />
          </order-limit-warning>
        </v-col>
      </v-row>
    </v-col>
    <sf-refresh-dialog
      v-if="orderOutdated"
      :title="$t('orderOutdated.title')"
      :description="$t('orderOutdated.description')"
      :loading="loading"
      @refresh="refreshOrder"
    />
  </list-content>
</template>

<script setup lang="ts">
import OrderLimitWarning from '@/components/OrderLimitWarning.vue'
import RejectionDialog from '@/components/RejectionDialog.vue'
import SfAlertSuccess from '@/components/alerts/SfAlertSuccess.vue'
import SfAlertWarning from '@/components/alerts/SfAlertWarning.vue'
import SfLargeButton from '@/components/button/SfLargeButton.vue'
import SfRefreshDialog from '@/components/dialog/SfRefreshDialog.vue'
import ListContent from '@/components/list/ListContent.vue'
import SfText from '@/components/text/SfText.vue'
import useAddressValidation from '@/modules/address/useAddressValidation'
import ApprovalRequestInfo from '@/modules/order/components/ApprovalRequestInfo.vue'
import MobexPaymentMethod from '@/modules/order/components/MobexPaymentMethod.vue'
import OrderAddressAndContactData from '@/modules/order/components/OrderAddressAndContactData.vue'
import OrderComment from '@/modules/order/components/OrderComment.vue'
import OrderInfo from '@/modules/order/components/OrderInfo.vue'
import OrderNumbers from '@/modules/order/components/OrderNumbers.vue'
import OrderPosition from '@/modules/order/components/OrderPosition.vue'
import OrderValidationStatus from '@/modules/order/components/OrderValidationStatus.vue'
import { isOrderApprovableByMe } from '@/modules/order/helpers'
import useCheckout from '@/modules/order/useCheckout'
import useOrder from '@/modules/order/useOrder'
import useOrderingConditions from '@/modules/portalSettings/useOrderingConditions'
import usePortalSettings from '@/modules/portalSettings/usePortalSettings'
import OrderEvent from '@/modules/tracking/events/order'
import { trackEvent } from '@/modules/tracking/useTracking'
import useOwnUser from '@/modules/user/useOwnUser'
import router from '@/router'
import {
  AddressType,
  ApprovalRequestStatus,
  ContactData,
  DetailedOrder,
  OrderLimitExceedance,
  OrderValidationResult,
  PortalOrderStatus,
  ShopAddress
} from '@/types/index'
import DOMPurify from 'dompurify'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

const form = ref<HTMLFormElement>()
const validForm = ref(false)
const currentRoute = useRoute()

const orderStatus = currentRoute.query.orderStatus
useCheckout().loadCheckoutTerms()

const { portalSettings } = storeToRefs(usePortalSettings())
const { orderingConditions } = storeToRefs(useOrderingConditions())
const { order, sending, rejecting, orderOutdated, loading, allCommentsValid } =
  storeToRefs(useOrder())
const { delivery, billing } = storeToRefs(useAddressValidation())
const { checkoutTerms } = storeToRefs(useCheckout())
const { isAdmin } = storeToRefs(useOwnUser())

const detailedOrder = computed<DetailedOrder>(() => order.value as DetailedOrder)
const addressValid = computed(() => delivery.value.valid && billing.value.valid)
const isPreparedOrder = computed(() => order.value?.orderStatus === PortalOrderStatus.PREPARING)
const isWaitingForTopiCheckout = computed(
  () => order.value?.orderStatus === PortalOrderStatus.IN_TOPI_CHECKOUT
)
const isTopiExpired = computed(() => order.value?.orderStatus === PortalOrderStatus.TOPI_EXPIRED)
const onlyOneItem = computed(() => order.value?.items.length === 1)
const rejectionDate = computed(() =>
  order.value?.approvalRequest?.status === ApprovalRequestStatus.REJECTED
    ? order.value?.approvalRequest?.processedDate
    : undefined
)
const isApproveableOrder = computed(() => {
  const orderRequest = order.value?.approvalRequest

  if (isAdmin.value()) {
    return true
  }

  if (orderRequest) {
    return isOrderApprovableByMe(orderRequest)
  }

  // default behaviour is to enable purchase
  return true
})

const removeQueryParams = () => {
  router.replace({ query: {} })
}

const sendOrder = () => {
  if (order.value && form.value && form.value.validate()) {
    if (order.value.approvalRequest) {
      trackEvent(OrderEvent.Approve)
    } else {
      trackEvent(OrderEvent.Send)
    }
    useOrder().sendExistingOrder(order.value.id)
  }
}

const requestOrder = (comment: string): void => {
  if (order.value) {
    if (order.value.approvalRequest) {
      trackEvent(OrderEvent.RequestApproval)
    } else {
      trackEvent(OrderEvent.Request)
    }
    useOrder().requestExistingOrder(order.value.id, comment)
  }
}

const declineOrder = (declineReason: string) => {
  if (order.value) {
    if (order.value.approvalRequest) {
      trackEvent(OrderEvent.DeclineApproval)
    } else {
      trackEvent(OrderEvent.Decline)
    }
    useOrder().declineOrder(order.value?.id, declineReason)
  }
}

const updateOrderNumber = (orderNumber: string) => {
  if (order.value) {
    useOrder().updateOrder(order.value.id, { orderNumber, timestamp: order.value.timestamp })
  }
}

const updateCustomerOrderNumber = (customerOrderId: string) => {
  if (order.value) {
    useOrder().updateOrder(order.value.id, {
      customerOrderId,
      timestamp: order.value.timestamp
    })
  }
}

const updateOrderComment = (comment: string) => {
  if (order.value) {
    useOrder().updateOrder(order.value.id, { comment, timestamp: order.value.timestamp })
  }
}

const updatePartialShipment = (hasPartialShipment: boolean) => {
  if (order.value) {
    useOrder().updateOrder(order.value.id, {
      hasPartialShipment,
      timestamp: order.value.timestamp
    })
  }
}

const updateBillingAddress = (billingAddress: ShopAddress) => {
  if (order.value) {
    order.value.billingAddress = billingAddress
    useOrder().updateOrder(order.value.id, { billingAddress, timestamp: order.value.timestamp })
  }
}

const updateDeliveryAddress = (deliveryAddress: ShopAddress) => {
  if (order.value) {
    order.value.deliveryAddress = deliveryAddress
    useOrder().updateOrder(order.value.id, {
      deliveryAddress,
      timestamp: order.value.timestamp
    })
  }
}

const updateBillingContactData = (billingContactData: ContactData) => {
  if (order.value) {
    order.value.billingContactData = billingContactData
    useOrder().updateOrder(order.value?.id, {
      billingContactData,
      timestamp: order.value.timestamp
    })
  }
}

const updateDeliveryContactData = (deliveryContactData: ContactData) => {
  if (order.value) {
    order.value.deliveryContactData = deliveryContactData
    useOrder().updateOrder(order.value?.id, {
      deliveryContactData,
      timestamp: order.value.timestamp
    })
  }
}

const refreshOrder = () => {
  useOrder().getOrder(currentRoute.params.id as string)
}

const safeCheckoutTerms = computed(() =>
  checkoutTerms.value ? DOMPurify.sanitize(checkoutTerms.value) : ''
)
</script>
